import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../../stores/gameStore';
import { useConversationStore } from '../../stores/conversationStore';
import { getApiUrl } from '../../lib/api';

interface Game {
  id: string;  // Changed from game_id to id to match API response
  name: string;
  description?: string;
  complexity?: string;
  rule_count: number;
  ai_tags?: string[];
}

export const GameGrid: React.FC = () => {
  const navigate = useNavigate();
  const { selectedGame, selectGame } = useGameStore();
  const { createNewConversation } = useConversationStore();

  const { data: gamesData, isLoading, error } = useQuery({
    queryKey: ['games'],
    queryFn: async () => {
      const response = await fetch(getApiUrl('/api/games/'));
      if (!response.ok) {
        throw new Error('Failed to load games');
      }
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const games = gamesData?.games || [];
  const featuredGames = games.slice(0, 4); // Show first 4 games as featured

  const handleGameSelect = async (game: Game) => {
    try {
      // Select the game in the store
      selectGame(game);
      
      // Create a new conversation for this game
      const conversation = await createNewConversation(game.id, game.name);
      
      // Navigate to chat with the new conversation
      navigate(`/chat/${game.id}/${conversation.id}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
      // Fallback to simple chat navigation
      selectGame(game);
      navigate(`/chat/${game.id}`);
    }
  };

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Featured Games
        </h3>
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-xl">⚠️</span>
          </div>
          <p className="text-gray-500 text-sm">
            Failed to load games. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Featured Games
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Featured Games
        </h3>
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-gray-400 text-xl">🎲</span>
          </div>
          <p className="text-gray-500 text-sm">
            No games available. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Featured Games
        </h3>
        <button
          onClick={() => navigate('/games')}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          View all games
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {featuredGames.map((game: Game) => (
          <div
            key={game.id}
            onClick={() => handleGameSelect(game)}
            className={`p-4 rounded-lg border transition-all cursor-pointer hover:shadow-md ${
              selectedGame?.id === game.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm">
                  {game.id === 'chess' ? '♟️' : '🎲'}
                </div>
                <h4 className="text-sm font-medium text-gray-900">
                  {game.name}
                </h4>
              </div>
              
              {selectedGame?.id === game.id && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Selected
                </span>
              )}
            </div>
            
            <p className="text-xs text-gray-600 mb-3 line-clamp-2">
              {game.description || 'No description available'}
            </p>
            
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-3">
                <span className="inline-flex items-center text-gray-500">
                  📋 {game.rule_count} rules
                </span>
                {game.complexity && (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full font-medium ${
                    game.complexity === 'easy' ? 'bg-green-100 text-green-800' :
                    game.complexity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {game.complexity}
                  </span>
                )}
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleGameSelect(game);
                }}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Start Chat →
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};